import classNames from "classnames";
import copy from "copy-to-clipboard";
import moment from "moment";
import React from "react";
import Duration from "~/components/shared/Duration";
import Icon from "~/components/shared/Icon";
import Spinner from "~/components/shared/Spinner";
import { Tooltip } from "~/components/shared/Tooltip";
import { DateLike, formatLogTimestamp, getDuration } from "~/lib/date";
import parseEmoji from "~/lib/parseEmoji";
import JobLogLineStore from "~/stores/JobLogLineStore";

/**
 * Class names for the cells that hold the actual log text. Search highlighting relies on these to
 * walk only the rendered log content and skip surrounding UI chrome (line-number tooltips, action
 * menus) whose hidden text would otherwise collide with the search term.
 */
export const LINE_CONTENT_CLASS = "JobLogOutputComponent__Line__Content";
export const GROUP_NAME_CLASS = "JobLogOutputComponent__Header__Name";

export type LogSettingsContextType = {
  timestampsOn: boolean;
  timestampsInUTC: boolean;
};

const LogSettingsContext = React.createContext<LogSettingsContextType>({
  timestampsOn: false,
  timestampsInUTC: false,
});

export { LogSettingsContext };

export function Log({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={classNames("JobLogOutputComponent", className, {
        // Flex column whose body (the log table) is the scroll viewport. Must be
        // able to shrink to the container height (min-h-0, no shrink-0) so the
        // table is bounded and scrolls internally. Drop the base bottom padding
        // so the scrolling table meets the footer flush.
        "flex min-h-0 flex-auto flex-col !rounded-none !pb-0": Features.BuildSidebar,
      })}
      {...props}
    >
      {children}
    </div>
  );
}

export function LogHeader({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={classNames("JobLogOutputComponent__Controls", className, {
        "z-10 !py-0": Features.BuildSidebar,
      })}
      {...props}
    >
      {children}
    </div>
  );
}

export function LogControl({
  children,
  active,
  ...props
}: (
  | React.ButtonHTMLAttributes<HTMLButtonElement>
  | ({ href: string } & React.AnchorHTMLAttributes<HTMLAnchorElement>)
) & {
  active?: boolean;
}) {
  const className = classNames(
    "JobLogOutputComponent__Button",
    { "JobLogOutputComponent__Button--Active": active },
    props.className,
  );

  if ("href" in props) {
    return (
      <a {...props} className={className}>
        {children}
      </a>
    );
  }

  return (
    <button {...props} className={className}>
      {children}
    </button>
  );
}

export function LogBody({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={classNames("JobLogOutputComponent__Body", className, {
        // Flex column so the log table fills the body and becomes the scroll
        // viewport. `min-h-0` lets it shrink below content height.
        "flex min-h-0 flex-auto flex-col": Features.BuildSidebar,
      })}
      {...props}
    >
      {children}
    </div>
  );
}

export function LogLoading({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={classNames("JobLogOutputComponent__Loader", className)} {...props}>
      {children}
    </div>
  );
}

export function LogMessage({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={classNames("p-4", className)} {...props}>
      {children}
    </div>
  );
}

export function LogContent({
  className,
  children,
  totalLines,
  ...props
}: React.HTMLAttributes<HTMLTableElement> & {
  totalLines?: number;
}) {
  const lineNumberWidth = totalLines?.toString().length ?? 0;

  const style = {
    "--line-number-width": `${Math.max(lineNumberWidth, 2)}ch`,
  } as React.CSSProperties;

  return (
    <table
      className={classNames("JobLogOutputComponent__Table", className)}
      style={style}
      {...props}
    >
      {children}
    </table>
  );
}

export function LogGroup({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tbody className={className} {...props}>
      {children}
    </tbody>
  );
}

export const LogGroupHeader = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement> & {
    expanded?: boolean;
    number?: number;
    highlighted?: boolean;
    deemphasized?: boolean;
  }
>(({ className, number, highlighted, expanded, deemphasized, children, ...props }, ref) => {
  const innerRef = useScrollToHighlighted(highlighted);

  return (
    <tr
      ref={ref ?? innerRef}
      aria-rowindex={number}
      aria-expanded={expanded}
      className={classNames(
        "JobLogOutputComponent__Header group/log-line relative scroll-mt-20 hover:z-[2] has-[[data-state=open]]:z-[2]",
        {
          "JobLogOutputComponent__Header--Deemphasized": deemphasized,
          "JobLogOutputComponent__Header--Highlighted": highlighted,
          "z-[1]": highlighted,
          "has-[[data-state=open]]:![background:var(--theme-bg-3)]": !highlighted,
        },
        className,
      )}
      {...props}
    >
      {children}
    </tr>
  );
});

export function LogGroupName({
  className,
  name,
  expanded,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & {
  name: string;
  expanded?: boolean;
}) {
  return (
    <td className={classNames(`${GROUP_NAME_CLASS} ph-no-capture`, className)} {...props}>
      <button
        className="float-left -ml-1 mr-1 flex h-5 select-none items-center"
        aria-expanded={expanded}
        aria-label={expanded ? `Collapse ${name}` : `Expand ${name}`}
      >
        <Icon
          icon="heroicons/16/solid/chevron-right"
          className={classNames("h-4 w-4 transition-transform duration-75", {
            "rotate-90": expanded,
          })}
        />
      </button>

      <span
        dangerouslySetInnerHTML={{
          __html: parseEmoji(name, { escape: false }),
        }}
      />
    </td>
  );
}

function useScrollToHighlighted(_highlighted?: boolean) {
  // Scrolling is handled at the page level to prevent race conditions
  return React.useRef<HTMLTableRowElement>(null);
}

export const LogLine = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement> & {
    number?: number;
    highlighted?: boolean;
  }
>(({ className, number, highlighted, children, ...props }, ref) => {
  const innerRef = useScrollToHighlighted(highlighted);

  return (
    <tr
      ref={ref ?? innerRef}
      aria-rowindex={number}
      className={classNames(
        "JobLogOutputComponent__Line group/log-line relative scroll-mt-20 hover:z-[2] has-[[data-state=open]]:z-[2]",
        {
          "JobLogOutputComponent__Line--Highlighted": highlighted,
          "z-[1]": highlighted,
        },
        className,
      )}
      {...props}
    >
      {children}
    </tr>
  );
});

export function LogLineContent({
  className,
  children,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td className={classNames(`${LINE_CONTENT_CLASS} ph-no-capture`, className)} {...props}>
      {children}
    </td>
  );
}

export function LogLineNumber({
  className,
  number,
  actions,
  jobId,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & {
  number: number;
  actions?: React.ReactNode;
  jobId: string;
}) {
  const anchor = JobLogLineStore.getAnchorLine();
  const lineNumberTitle = (() => {
    if (anchor?.jobId === jobId && anchor.lineNumber !== number) {
      const start = Math.min(anchor.lineNumber, number);
      const end = Math.max(anchor.lineNumber, number);
      return (
        <>
          <div>
            Select line <span className="font-medium">{number}</span>
          </div>
          <div>
            (<span className="font-medium italic">Shift+click</span> select{" "}
            <span className="font-medium">
              L{start}
              -L{end}
            </span>
            )
          </div>
        </>
      );
    }
    return (
      <>
        Select line <span className="font-medium">{number}</span>
      </>
    );
  })();

  return (
    <td
      className={classNames(
        "JobLogOutputComponent__Number relative w-[--line-number-width] pl-10 has-[[data-state=open]]:![color:var(--theme-text-1)]",
        className,
      )}
      {...props}
    >
      {actions && (
        <div className="absolute inset-y-0 left-2.5 z-50 flex items-start opacity-0 focus-within:opacity-100 group-hover/log-line:opacity-100 has-[[data-state=open]]:opacity-100">
          {actions}
        </div>
      )}
      <Tooltip tooltip={lineNumberTitle}>
        <span data-line-number={number} className="before:content-[attr(data-line-number)]" />
      </Tooltip>
    </td>
  );
}

export function LogTimestamp({
  className,
  time,
  utc,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & {
  time?: DateLike | null;
  utc?: boolean | null;
}) {
  const timestamp = time ? formatLogTimestamp(time, utc ?? false) : null;

  return (
    <td
      className={classNames(
        "JobLogOutputComponent__Timestamp",
        { "JobLogOutputComponent__Timestamp--UTC": utc },
        className,
      )}
      {...props}
    >
      {timestamp}
    </td>
  );
}

// Once per second this broadcasts how the duration bars should grow while a
// group is running, off a single shared timer:
//   - runningElapsedMs: the running group's elapsed time, used to grow its bar.
//   - scale: a factor (<= 1) applied to every finished group's bar so they
//     shrink uniformly once the running group's elapsed time overtakes the
//     longest finished group.
type RunningDurationValue = {
  scale: number;
  runningElapsedMs: number;
};

const RunningDurationContext = React.createContext<RunningDurationValue>({
  scale: 1,
  runningElapsedMs: 0,
});

export { RunningDurationContext };

// Derives the provider props from a list of parsed groups. The parser tags the
// single running group with `maxDuration` (the longest finished duration).
export function runningDurationProps(
  groups: ReadonlyArray<{
    startedAt?: DateLike | null;
    maxDuration?: number | null;
  }>,
): { startedAt?: DateLike | null; longestFinishedDuration?: number | null } {
  const running = groups.find((group) => group.maxDuration != null);
  return {
    startedAt: running?.startedAt,
    longestFinishedDuration: running?.maxDuration,
  };
}

export function RunningDurationProvider({
  startedAt,
  longestFinishedDuration,
  children,
}: {
  startedAt?: DateLike | null;
  longestFinishedDuration?: number | null;
  children: React.ReactNode;
}) {
  // A tick counter drives a re-render once a second; the value itself is
  // derived during render (below) rather than stored in state. This matters
  // when the running group changes: `startedAt` updates and the new value is
  // computed in the same render, so consumers never read a stale elapsed time
  // from the previous running group.
  const [tick, setTick] = React.useState(0);

  React.useEffect(() => {
    if (!startedAt) {
      return;
    }
    const interval = setInterval(() => setTick((current) => current + 1), 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  const value = React.useMemo<RunningDurationValue>(() => {
    if (!startedAt) {
      return { scale: 1, runningElapsedMs: 0 };
    }
    // Header times are formatted as UTC without an offset, so parse as UTC
    // before diffing against the local `now`; otherwise the elapsed time is off
    // by the viewer's timezone offset. (Epoch-ms starts are absolute either way.)
    const runningElapsedMs = getDuration(moment.utc(startedAt)).asMilliseconds();
    const longest = longestFinishedDuration ?? 0;
    // Until the running group overtakes the longest finished group, finished
    // bars are unscaled; after that the denominator becomes the running group's
    // own elapsed time, shrinking the finished bars in proportion.
    const scale = longest > 0 && runningElapsedMs > longest ? longest / runningElapsedMs : 1;
    return { scale, runningElapsedMs };
    // `tick` isn't read in the body but belongs in the deps: it's what makes
    // the memo re-sample the clock each second. Keeping the memo (rather than
    // computing inline) avoids re-rendering every log row on unrelated updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startedAt, longestFinishedDuration, tick]);

  return (
    <RunningDurationContext.Provider value={value}>{children}</RunningDurationContext.Provider>
  );
}

export function LogGroupTime({
  startedAt,
  finishedAt,
  finished,
  durationPercentage,
  maxDuration,
  className,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & {
  startedAt?: DateLike | null;
  finishedAt?: DateLike | null;
  finished: boolean;
  durationPercentage?: number | null;
  maxDuration?: number | null;
}) {
  const { scale, runningElapsedMs } = React.useContext(RunningDurationContext);

  // The running group has no fixed durationPercentage; grow it live. Finished
  // groups use their static percentage, scaled down as the running group pulls
  // ahead so the running bar stays the widest.
  const isRunning = !finishedAt && maxDuration != null;
  let percentage: number | null | undefined;
  if (isRunning) {
    // With no finished group to scale against, the running group is the longest
    // by definition, so show a full bar.
    percentage =
      !maxDuration || maxDuration <= 0
        ? 100
        : Math.min(100, Math.floor((runningElapsedMs / maxDuration) * 100));
  } else if (durationPercentage != null) {
    percentage = Math.round(durationPercentage * scale);
  }

  // Set the minimum width of the badge to its percentage of time taken to run
  // the group, so the bar grows as the group's duration increases. A 0% width
  // is a real value (a just-started running group), not "no bar". Unfinished
  // groups without the live treatment (no maxDuration — e.g. the non-virtualised
  // log output) keep the static spinner-sized badge.
  const badgeStyle: React.CSSProperties =
    percentage != null
      ? { minWidth: `${percentage}%` }
      : startedAt && !finishedAt
        ? { width: "20px" }
        : {};

  // Only animate continuous, context-driven updates (the per-second growth and
  // shrink). Snap on data changes — initial load and each 5s re-parse alter the
  // props, and animating those would make the bar slide from a stale or
  // placeholder width (e.g. the brief no-finished-groups full bar on load).
  const propsKey = `${startedAt}|${finishedAt}|${durationPercentage}|${maxDuration}`;
  const prevPropsKey = React.useRef<string | null>(null);
  if (prevPropsKey.current !== propsKey) {
    badgeStyle.transition = "none";
  }
  React.useEffect(() => {
    prevPropsKey.current = propsKey;
  });

  return (
    <td className={classNames("JobLogOutputComponent__Time", className)} {...props}>
      {startedAt && (
        <span className="JobLogOutputComponent__Time__Badge text-nowrap" style={badgeStyle}>
          {!finished && <Spinner color={false} size="8px" className="py-0.5" />}

          {/* Show a static duration for finished groups, or a live one for the
              genuine running group (gated on isRunning, not just !finished, so
              an interior group with a missing/null next header time doesn't tick
              up to now indefinitely). The running start is parsed as UTC so the
              live elapsed isn't skewed by the viewer's timezone. */}
          {finishedAt ? (
            <Duration.Short from={startedAt} to={finishedAt} />
          ) : isRunning ? (
            <Duration.Short from={moment.utc(startedAt)} />
          ) : null}
        </span>
      )}
    </td>
  );
}
export function LogCopyPermalink({
  jobId,
  lineNumber,
  label,
}: {
  jobId: string;
  lineNumber: number;
  label: string;
}) {
  const [copied, setCopied] = React.useState(false);

  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      // Prevent the line from being unhighlighted
      event.stopPropagation();

      const url = new URL(window.location.href);

      // Check if we have a range highlighted
      const range = JobLogLineStore.getHighlightedRange(jobId);
      if (range && range.start !== range.end) {
        // Range format: #jobId/L{start}-L{end}
        url.hash = `${jobId}/L${range.start}-L${range.end}`;
        copy(url.toString());
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
        return;
      }

      // Line-only format: #jobId/L{lineNumber}
      url.hash = `${jobId}/L${lineNumber}`;

      copy(url.toString());
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    },
    [jobId, lineNumber],
  );

  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={handleClick}
      className="btn btn-depth btn-small before:shadow-depth-150 relative flex h-6 w-6 items-center justify-center bg-white p-0"
    >
      {copied ? (
        <Icon icon="heroicons/16/solid/check" className="size-4 text-green-700" />
      ) : (
        <Icon icon="heroicons/16/solid/link" className="size-4" />
      )}
    </button>
  );
}
